import { Card, CardContent } from '../ui/card';
import { Users, Target, TrendingUp, DollarSign, AlertCircle } from 'lucide-react';
import { UtmAnalyticsResponse } from '../../types';

interface OverviewCardsProps {
  analytics: UtmAnalyticsResponse | null;
  loading: boolean;
}

export function OverviewCards({ analytics, loading }: OverviewCardsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  // Defensive: use nullish coalescing for safe access
  const totalLeads = analytics.totalLeads ?? 0;
  const totalConversions = analytics.totalConversions ?? 0;
  const conversionRate = analytics.conversionRate ?? 0;
  const totalQuoteValue = analytics.totalQuoteValue ?? 0;
  const unsyncedToPiperun = analytics.unsyncedToPiperun ?? 0;

  const cards = [
    {
      title: 'Total de Leads',
      value: totalLeads.toLocaleString('pt-BR'),
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Conversões',
      value: totalConversions.toLocaleString('pt-BR'),
      icon: Target,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Taxa de Conversão',
      value: `${conversionRate.toFixed(1)}%`,
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Valor Total',
      value: new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(totalQuoteValue),
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Não Sincronizados',
      value: unsyncedToPiperun.toLocaleString('pt-BR'),
      icon: AlertCircle,
      color: unsyncedToPiperun > 0 ? 'text-amber-600' : 'text-gray-400',
      bgColor: unsyncedToPiperun > 0 ? 'bg-amber-50' : 'bg-gray-50'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {cards.map((card) => (
        <Card key={card.title} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                <p className="text-2xl font-bold mt-1">{card.value}</p>
              </div>
              <div className={`p-3 rounded-full ${card.bgColor}`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
